import { DefaultRootState } from 'react-redux';
import { Route } from 'react-router';
import axios, { AxiosError } from 'axios';
import { routerMiddleware, connectRouter } from 'connected-react-router';
import { BrowserHistoryBuildOptions } from 'history';
import { createStore, applyMiddleware, compose, combineReducers, Store, Dispatch } from 'redux';
import thunkMiddleware from 'redux-thunk';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import batchMiddleware, { batchReducer } from '@hh.ru/redux-batch-middleware';
import { makeSetStoreField, SET_FIELD_DATA_AS_IS, createRootReducer } from '@hh.ru/redux-create-reducer';
import makeSPAMiddleware from '@hh.ru/redux-spa-middleware';
import mutate from '@hh.ru/redux-spa-middleware/lib/mutators';

import { RootReducer, AppRoutes } from '../../types';
import reducer from './baseReducer';
import fetcher from './fetcher';
import getNoTrlValue from './getNoTrlValue';
import { getReactStateNode } from './getRootNode';
import {
    HHTM_SOURCE_HEADER,
    HHTM_FROM_HEADER,
    HHTM_FROM_LABEL_HEADER,
    HHTM_SOURCE_LABEL_HEADER,
} from './hhtmInterceptor';
import getOrCreateHistory from './history';
import { getSentryClient } from './initSentry';
import { getServiceName } from './serviceName';

const composeEnhancers = (typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

const fetchDataErrorAction = makeSetStoreField('fetchDataError');
const SPAErrorAction = makeSetStoreField('SPAError');

interface MakeStoreParams {
    identifier: string;
    appReducer: RootReducer;
    routes: AppRoutes;
    disableSpaMiddleware?: boolean;
    browserHistoryOptions?: BrowserHistoryBuildOptions;
}

const makeStore = ({
    identifier,
    appReducer,
    routes,
    disableSpaMiddleware,
    browserHistoryOptions,
}: MakeStoreParams): Store | null => {
    const serviceName = getServiceName();
    const initialStateNode = getReactStateNode(identifier);

    if (!initialStateNode) {
        return null;
    }

    let initialState: DefaultRootState | null = null;
    try {
        const stateFromHTML = initialStateNode.content?.textContent || initialStateNode?.innerText;
        initialState = JSON.parse(stateFromHTML) as DefaultRootState;
    } catch (_) {
        return null;
    }

    /**
     * У прокси компонентов нельзя использовать SPA Middleware, при смене hash в урле она начнет, работать.
     * В случае с прокси компонентами роутинг никогда не должен использоваться
     */
    const needSPAMiddleware = !initialState?.isProxiedComponent && !disableSpaMiddleware;

    const rootReducer = createRootReducer({
        ...reducer,
        ...appReducer,
    });

    const middlewares = [thunkMiddleware, batchMiddleware, routerMiddleware(getOrCreateHistory(browserHistoryOptions))];

    if (needSPAMiddleware) {
        const showDefaultError = (error: AxiosError) => {
            return (dispatch: Dispatch) => {
                if (axios.isCancel(error) || error?.response?.status === 406) {
                    return;
                }
                if (!error?.response) {
                    dispatch(SPAErrorAction('network'));
                    return;
                }
                dispatch(SPAErrorAction('default'));
            };
        };
        middlewares.unshift(
            makeSPAMiddleware({
                fetcher,
                routes: routes as Route[],
                headers: {
                    [HHTM_SOURCE_HEADER]: '',
                    [HHTM_FROM_HEADER]: window.globalServiceVars[serviceName].analyticsParams.hhtmSource,
                    [HHTM_SOURCE_LABEL_HEADER]: window.globalServiceVars[serviceName].analyticsParams
                        .hhtmSourceLabel as string,
                    [HHTM_FROM_LABEL_HEADER]: window.globalServiceVars[serviceName].analyticsParams
                        .hhtmFromLabel as string,
                },
                rootReducer,
                makeSetStoreField,
                setFieldDataAsIs: SET_FIELD_DATA_AS_IS,
                fetchDataErrorAction,
                resetSelectors: ['pageError'],
                globalVars: window.globalServiceVars[serviceName],
                reactRoot: null,
                showDefaultError,
            })
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const enhancers = [applyMiddleware(...middlewares)];
    if (process.env.NODE_ENV !== 'test' && window.globalServiceVars[serviceName].features.sentry_logging) {
        const createSentryReduxEnhancer = getSentryClient()?.createSentryReduxEnhancer;
        if (createSentryReduxEnhancer) {
            enhancers.push(
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                createSentryReduxEnhancer()
            );
        }
    }

    const history = getOrCreateHistory(browserHistoryOptions);
    return createStore(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-call
        batchReducer(
            combineReducers({
                ...rootReducer,
                router: connectRouter(history),
            })
        ),
        mutate(initialState, { noTrl: getNoTrlValue(history) }),
        composeEnhancers(...enhancers)
    );
};

export default makeStore;
